/**
 * Central Notification Preferences REST Client
 *
 * Direct browser → Central calls (Bearer token). Shared types/paths from
 * @levelup/central-notifications — no local duplication.
 */

import {
  NOTIFICATION_PREFERENCES_PATH,
} from '@levelup/central-notifications';
import type {
  CategoryPreference,
  PreferencesResponse,
  UpdateCategoryResponse,
} from '@levelup/central-notifications';
import { getAccessToken } from './centralAuth';

const RAW_CENTRAL_URL = import.meta.env.VITE_CENTRAL_URL;

function getCentralBase(): string | null {
  if (!RAW_CENTRAL_URL) return null;
  return RAW_CENTRAL_URL.replace(/\/+$/, '');
}

async function prefsFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getAccessToken();
  if (!token) throw new Error('Not authenticated');
  const base = getCentralBase();
  if (!base) throw new Error('VITE_CENTRAL_URL not configured');

  const url = new URL(path, base).toString();
  const response = await fetch(url, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) throw new Error(`Central returned ${response.status}`);
  return response.json();
}

export async function fetchNotificationPreferences(): Promise<CategoryPreference[]> {
  const res = await prefsFetch<PreferencesResponse>(NOTIFICATION_PREFERENCES_PATH);
  return res.preferences ?? [];
}

export async function updateNotificationCategory(
  category: string,
  channel: string,
  enabled: boolean,
): Promise<CategoryPreference> {
  const res = await prefsFetch<UpdateCategoryResponse>(
    `${NOTIFICATION_PREFERENCES_PATH}/${category}`,
    {
      method: 'PATCH',
      body: JSON.stringify({ channels: { [channel]: enabled } }),
    },
  );
  return res.preference;
}
